import { useEffect, useLayoutEffect, useRef, type TextareaHTMLAttributes } from 'react';
import { caretDelta, fitHeight, type Reason } from './caret';

/**
 * A notes field that grows with what is typed in it.
 *
 * The old way — height 'auto', then the scroll height — shrank the field
 * to one row for a moment on every keystroke. Inside a sheet that shrinks
 * the whole panel, the panel's scroll position snaps up, then the field
 * grows back: on an iPhone in Telegram the text visibly jumped on every
 * Enter. Here the height is measured on an off-screen copy, so the real
 * field only ever gets its new height, and then the line with the cursor
 * is scrolled into the part of the screen the keyboard leaves visible.
 */
export function GrowArea({ value, style, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    fit(el);
    if (document.activeElement === el) keepCaretInView(el, 'typing');
  }, [value]);

  // Fits again when the width changes (rotation, the sheet opening).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let width = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== width) { width = el.clientWidth; fit(el); }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // While focused: room at the end of a sheet with a pinned bar, and the
  // field placed again whenever the keyboard changes the visible area.
  const cleanup = useRef<(() => void) | null>(null);
  useEffect(() => () => cleanup.current?.(), []);
  function onFocus(el: HTMLTextAreaElement) {
    cleanup.current?.();
    const sheet = el.closest('.psheet');
    sheet?.classList.add('psheet--typing');
    const vv = window.visualViewport;
    const placed = () => keepCaretInView(el, 'focus');
    const again = () => keepCaretInView(el, 'resize');
    vv?.addEventListener('resize', again);
    window.addEventListener('resize', again);
    // After the keyboard has opened, and once more after iOS's own scroll.
    const timers = [setTimeout(placed, 80), setTimeout(placed, 350)];
    cleanup.current = () => {
      sheet?.classList.remove('psheet--typing');
      vv?.removeEventListener('resize', again);
      window.removeEventListener('resize', again);
      timers.forEach(clearTimeout);
      cleanup.current = null;
    };
  }

  return (
    <textarea ref={ref} value={value} {...rest}
      onFocus={(e) => { rest.onFocus?.(e); onFocus(e.target); }}
      onBlur={(e) => { rest.onBlur?.(e); cleanup.current?.(); }}
      style={{ ...style, overflow: 'hidden', resize: 'none' }} />
  );
}

let twin: HTMLTextAreaElement | null = null;

/** The off-screen copy, styled like the field being measured. */
function twinOf(el: HTMLTextAreaElement) {
  if (!twin) {
    twin = document.createElement('textarea');
    twin.setAttribute('aria-hidden', 'true');
    twin.tabIndex = -1;
    Object.assign(twin.style, {
      position: 'absolute', top: '-9999px', left: '-9999px', visibility: 'hidden',
      overflow: 'hidden', height: '0', minHeight: '0', maxHeight: 'none', pointerEvents: 'none',
    });
    document.body.appendChild(twin);
  }
  const cs = getComputedStyle(el);
  for (const p of ['boxSizing', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing',
    'lineHeight', 'textTransform', 'wordSpacing', 'textIndent', 'whiteSpace', 'wordBreak',
    'overflowWrap', 'tabSize', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight',
    'borderTopWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderRightWidth', 'borderStyle'] as const) {
    (twin.style as any)[p] = cs[p];
  }
  twin.style.width = `${el.getBoundingClientRect().width}px`;
  return { twin, cs };
}

/** Height the text needs, never less than the field's rows. */
function fit(el: HTMLTextAreaElement) {
  const { twin, cs } = twinOf(el);
  const border = cs.boxSizing === 'border-box'
    ? parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth) : 0;
  const pad = cs.boxSizing === 'border-box' ? 0 : -(parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom));
  twin.rows = el.rows || 1;
  twin.value = el.value || el.placeholder || ' ';
  const need = twin.scrollHeight + border + pad;
  twin.value = '';
  // The rows the field asked for are its minimum.
  const minH = twin.scrollHeight + border + pad;
  const h = `${fitHeight({
    twinNeed: need, twinMin: minH,
    scrollHeight: el.scrollHeight, clientHeight: el.clientHeight, offsetHeight: el.offsetHeight,
  })}px`;
  if (el.style.height !== h) el.style.height = h;
  // Nothing hidden inside the field to scroll to.
  if (el.scrollTop !== 0) el.scrollTop = 0;
}

/** Where the cursor's line ends, from the top of the field. */
function caretBottom(el: HTMLTextAreaElement) {
  const { twin, cs } = twinOf(el);
  twin.rows = 1;
  twin.value = el.value.slice(0, el.selectionEnd ?? el.value.length) || ' ';
  const y = twin.scrollHeight + parseFloat(cs.borderTopWidth) - parseFloat(cs.paddingBottom);
  twin.value = '';
  return y;
}

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

/**
 * Scroll just enough that the cursor's line sits comfortably above the
 * keyboard. Measured against the visual viewport, which is what the
 * keyboard shrinks on iOS; the layout viewport stays full height.
 *
 * In the rating sheet and the notes sheet the cursor's line keeps two
 * lines of room above whatever covers the rest: the pinned Later / Finish
 * bar, or the keyboard. A line flush against the buttons is hard to read
 * and easy to mis-tap. On focus the field's label stays in sight too, when
 * there is room for both; the cursor wins when there is not. Elsewhere it
 * keeps a small margin, as before.
 */
function keepCaretInView(el: HTMLTextAreaElement, reason: Reason) {
  const vv = window.visualViewport;
  const viewTop = vv ? vv.offsetTop : 0;
  const viewBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
  const box = el.getBoundingClientRect();
  const lineH = parseFloat(getComputedStyle(el).lineHeight) || 20;
  const caret = box.top + caretBottom(el);
  const scroller = scrollParent(el);
  const frame = scroller?.getBoundingClientRect();
  // A pinned action bar at the bottom of the sheet covers the lines under it.
  const foot = scroller?.querySelector(':scope > .psheet__foot') as HTMLElement | null;
  const footTop = foot ? foot.getBoundingClientRect().top : Infinity;
  const visTop = Math.max(viewTop, frame?.top ?? viewTop);
  const visBottom = Math.min(viewBottom, frame?.bottom ?? viewBottom, footTop);
  const roomy = el.closest('.psheet, .sheet--rate') !== null;
  // The label right above the field, when it sits close to it.
  const label = el.previousElementSibling as HTMLElement | null;
  const labelBox = label?.getBoundingClientRect();
  const cs = getComputedStyle(el);
  const delta = caretDelta({
    visTop, visBottom, lineH, caret, boxTop: box.top,
    lastLine: box.bottom - parseFloat(cs.paddingBottom) - parseFloat(cs.borderBottomWidth),
    labelTop: labelBox && box.top - labelBox.bottom < 24 ? labelBox.top : null,
    roomy,
  }, reason);
  if (delta === 0) return;
  if (scroller) scroller.scrollTop += delta;
  else window.scrollBy(0, delta);
}
